import React, { useEffect, useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import api from '../../api/axios';
import './WebsiteContent.css';

const emptyContent = () => ({
  statistics: {
    verifiedVendors: 0,
    cities: 0,
    categories: 0,
    successfulEnquiries: 0,
    verifiedVendorsLabel: 'Verified Vendors',
    citiesLabel: 'Cities',
    categoriesLabel: 'Categories',
    successfulEnquiriesLabel: 'Successful Enquiries',
  },
  socialLinks: {
    facebook: '',
    instagram: '',
    twitter: '',
    linkedin: '',
    youtube: '',
    whatsapp: '',
  },
  footer: {
    description: '',
    email: '',
    phone: '',
    copyrightText: '',
    quickLinks: [],
    categoryLinks: [],
  },
  whyChooseUs: {
    title: 'Why Choose Us',
    subtitle: '',
    items: [],
  },
  successStories: {
    title: 'Vendor Success Stories',
    subtitle: '',
    items: [],
  },
});

const WebsiteContent = () => {
  const [form, setForm] = useState(emptyContent());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/website-content');
      const data = res.data?.data || res.data || {};
      setForm({
        ...emptyContent(),
        ...data,
        statistics: { ...emptyContent().statistics, ...(data.statistics || {}) },
        socialLinks: { ...emptyContent().socialLinks, ...(data.socialLinks || {}) },
        footer: {
          ...emptyContent().footer,
          ...(data.footer || {}),
          quickLinks: Array.isArray(data.footer?.quickLinks) ? data.footer.quickLinks : [],
          categoryLinks: Array.isArray(data.footer?.categoryLinks)
            ? data.footer.categoryLinks
            : [],
        },
        whyChooseUs: {
          ...emptyContent().whyChooseUs,
          ...(data.whyChooseUs || {}),
          items: Array.isArray(data.whyChooseUs?.items) ? data.whyChooseUs.items : [],
        },
        successStories: {
          ...emptyContent().successStories,
          ...(data.successStories || {}),
          items: Array.isArray(data.successStories?.items) ? data.successStories.items : [],
        },
      });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load website content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setStat = (key, value) => {
    setForm((prev) => ({
      ...prev,
      statistics: { ...prev.statistics, [key]: value },
    }));
  };

  const setSocial = (key, value) => {
    setForm((prev) => ({
      ...prev,
      socialLinks: { ...prev.socialLinks, [key]: value },
    }));
  };

  const setFooter = (key, value) => {
    setForm((prev) => ({
      ...prev,
      footer: { ...prev.footer, [key]: value },
    }));
  };

  const updateLinkList = (listKey, index, field, value) => {
    setForm((prev) => {
      const list = [...(prev.footer[listKey] || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, footer: { ...prev.footer, [listKey]: list } };
    });
  };

  const addLink = (listKey) => {
    setForm((prev) => ({
      ...prev,
      footer: {
        ...prev.footer,
        [listKey]: [...(prev.footer[listKey] || []), { label: '', url: '' }],
      },
    }));
  };

  const removeLink = (listKey, index) => {
    setForm((prev) => ({
      ...prev,
      footer: {
        ...prev.footer,
        [listKey]: (prev.footer[listKey] || []).filter((_, i) => i !== index),
      },
    }));
  };

  const updateWhyItem = (index, field, value) => {
    setForm((prev) => {
      const items = [...(prev.whyChooseUs.items || [])];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, whyChooseUs: { ...prev.whyChooseUs, items } };
    });
  };

  const addWhyItem = () => {
    setForm((prev) => ({
      ...prev,
      whyChooseUs: {
        ...prev.whyChooseUs,
        items: [
          ...(prev.whyChooseUs.items || []),
          { title: '', description: '', icon: '', order: (prev.whyChooseUs.items || []).length },
        ],
      },
    }));
  };

  const removeWhyItem = (index) => {
    setForm((prev) => ({
      ...prev,
      whyChooseUs: {
        ...prev.whyChooseUs,
        items: (prev.whyChooseUs.items || []).filter((_, i) => i !== index),
      },
    }));
  };

  const updateStory = (index, field, value) => {
    setForm((prev) => {
      const items = [...(prev.successStories.items || [])];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, successStories: { ...prev.successStories, items } };
    });
  };

  const addStory = () => {
    setForm((prev) => ({
      ...prev,
      successStories: {
        ...prev.successStories,
        items: [
          ...(prev.successStories.items || []),
          {
            name: '',
            role: '',
            vendorName: '',
            quote: '',
            rating: 5,
            image: '',
            order: (prev.successStories.items || []).length,
            isActive: true,
          },
        ],
      },
    }));
  };

  const removeStory = (index) => {
    setForm((prev) => ({
      ...prev,
      successStories: {
        ...prev.successStories,
        items: (prev.successStories.items || []).filter((_, i) => i !== index),
      },
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        statistics: {
          ...form.statistics,
          verifiedVendors: Number(form.statistics.verifiedVendors) || 0,
          cities: Number(form.statistics.cities) || 0,
          categories: Number(form.statistics.categories) || 0,
          successfulEnquiries: Number(form.statistics.successfulEnquiries) || 0,
        },
        socialLinks: Object.fromEntries(
          Object.entries(form.socialLinks).map(([k, v]) => [k, v || null])
        ),
        footer: {
          ...form.footer,
          description: form.footer.description || null,
          email: form.footer.email || null,
          phone: form.footer.phone || null,
          copyrightText: form.footer.copyrightText || null,
          quickLinks: (form.footer.quickLinks || []).filter((l) => l.label && l.url),
          categoryLinks: (form.footer.categoryLinks || []).filter((l) => l.label && l.url),
        },
        whyChooseUs: {
          title: form.whyChooseUs.title || 'Why Choose Us',
          subtitle: form.whyChooseUs.subtitle || null,
          items: (form.whyChooseUs.items || []).map((item, i) => ({
            ...item,
            icon: item.icon || null,
            order: Number(item.order) || i,
          })),
        },
        successStories: {
          title: form.successStories.title || 'Vendor Success Stories',
          subtitle: form.successStories.subtitle || null,
          items: (form.successStories.items || []).map((item, i) => ({
            ...item,
            image: item.image || null,
            rating: Number(item.rating) || 5,
            order: Number(item.order) || i,
            isActive: item.isActive !== false,
          })),
        },
      };

      await api.put('/admin/website-content', payload);
      toast.success('Website content saved successfully');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save website content');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="wc-page">
        <ToastContainer />
        <p className="wc-loading">Loading website content…</p>
      </div>
    );
  }

  return (
    <div className="wc-page">
      <ToastContainer position="top-right" />
      <div className="wc-header">
        <div>
          <h1>Website Content</h1>
          <p>
            Manage About page statistics, Why Choose Us, Success Stories, footer copy,
            and social links. Banner, Gallery, and Help &amp; Support stay in their own
            modules.
          </p>
        </div>
        <button type="button" className="wc-btn primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save All'}
        </button>
      </div>

      <form className="wc-form" onSubmit={handleSave}>
        <section className="wc-card">
          <h2>Statistics</h2>
          <p className="wc-hint">Use 0 to let the API compute live counts automatically.</p>
          <div className="wc-grid-4">
            {[
              ['verifiedVendors', 'verifiedVendorsLabel', 'Verified Vendors'],
              ['cities', 'citiesLabel', 'Cities'],
              ['categories', 'categoriesLabel', 'Categories'],
              ['successfulEnquiries', 'successfulEnquiriesLabel', 'Successful Enquiries'],
            ].map(([numKey, labelKey, fallback]) => (
              <div className="wc-field" key={numKey}>
                <label>{fallback} value</label>
                <input
                  type="number"
                  min="0"
                  value={form.statistics[numKey] ?? 0}
                  onChange={(e) => setStat(numKey, e.target.value)}
                />
                <label className="wc-sublabel">Label</label>
                <input
                  type="text"
                  value={form.statistics[labelKey] || ''}
                  onChange={(e) => setStat(labelKey, e.target.value)}
                  placeholder={fallback}
                />
              </div>
            ))}
          </div>
        </section>

        <section className="wc-card">
          <h2>Social Links</h2>
          <div className="wc-grid-3">
            {['facebook', 'instagram', 'twitter', 'linkedin', 'youtube', 'whatsapp'].map((key) => (
              <div className="wc-field" key={key}>
                <label>{key.charAt(0).toUpperCase() + key.slice(1)}</label>
                <input
                  type="url"
                  value={form.socialLinks[key] || ''}
                  onChange={(e) => setSocial(key, e.target.value)}
                  placeholder={`https://…`}
                />
              </div>
            ))}
          </div>
        </section>

        <section className="wc-card">
          <h2>Footer Content</h2>
          <div className="wc-grid-2">
            <div className="wc-field full">
              <label>Description</label>
              <textarea
                rows={3}
                value={form.footer.description || ''}
                onChange={(e) => setFooter('description', e.target.value)}
              />
            </div>
            <div className="wc-field">
              <label>Email</label>
              <input
                type="email"
                value={form.footer.email || ''}
                onChange={(e) => setFooter('email', e.target.value)}
              />
            </div>
            <div className="wc-field">
              <label>Phone</label>
              <input
                type="text"
                value={form.footer.phone || ''}
                onChange={(e) => setFooter('phone', e.target.value)}
              />
            </div>
            <div className="wc-field full">
              <label>Copyright text</label>
              <input
                type="text"
                value={form.footer.copyrightText || ''}
                onChange={(e) => setFooter('copyrightText', e.target.value)}
              />
            </div>
          </div>

          <div className="wc-subblock">
            <div className="wc-subhead">
              <h3>Quick Links</h3>
              <button type="button" className="wc-btn ghost" onClick={() => addLink('quickLinks')}>
                + Add link
              </button>
            </div>
            {(form.footer.quickLinks || []).map((link, index) => (
              <div className="wc-row" key={`ql-${index}`}>
                <input
                  placeholder="Label"
                  value={link.label || ''}
                  onChange={(e) => updateLinkList('quickLinks', index, 'label', e.target.value)}
                />
                <input
                  placeholder="/path or URL"
                  value={link.url || ''}
                  onChange={(e) => updateLinkList('quickLinks', index, 'url', e.target.value)}
                />
                <button type="button" className="wc-btn danger" onClick={() => removeLink('quickLinks', index)}>
                  Remove
                </button>
              </div>
            ))}
          </div>

          <div className="wc-subblock">
            <div className="wc-subhead">
              <h3>Popular Categories</h3>
              <button type="button" className="wc-btn ghost" onClick={() => addLink('categoryLinks')}>
                + Add category
              </button>
            </div>
            {(form.footer.categoryLinks || []).map((link, index) => (
              <div className="wc-row" key={`cl-${index}`}>
                <input
                  placeholder="Label"
                  value={link.label || ''}
                  onChange={(e) => updateLinkList('categoryLinks', index, 'label', e.target.value)}
                />
                <input
                  placeholder="/vendors?category=…"
                  value={link.url || ''}
                  onChange={(e) => updateLinkList('categoryLinks', index, 'url', e.target.value)}
                />
                <button type="button" className="wc-btn danger" onClick={() => removeLink('categoryLinks', index)}>
                  Remove
                </button>
              </div>
            ))}
          </div>
        </section>

        <section className="wc-card">
          <h2>Why Choose Us</h2>
          <div className="wc-grid-2">
            <div className="wc-field">
              <label>Title</label>
              <input
                value={form.whyChooseUs.title || ''}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    whyChooseUs: { ...prev.whyChooseUs, title: e.target.value },
                  }))
                }
              />
            </div>
            <div className="wc-field">
              <label>Subtitle</label>
              <input
                value={form.whyChooseUs.subtitle || ''}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    whyChooseUs: { ...prev.whyChooseUs, subtitle: e.target.value },
                  }))
                }
              />
            </div>
          </div>
          <div className="wc-subhead">
            <h3>Cards</h3>
            <button type="button" className="wc-btn ghost" onClick={addWhyItem}>
              + Add card
            </button>
          </div>
          {(form.whyChooseUs.items || []).map((item, index) => (
            <div className="wc-item" key={`why-${index}`}>
              <div className="wc-grid-3">
                <div className="wc-field">
                  <label>Title</label>
                  <input value={item.title || ''} onChange={(e) => updateWhyItem(index, 'title', e.target.value)} />
                </div>
                <div className="wc-field">
                  <label>Icon URL (optional)</label>
                  <input value={item.icon || ''} onChange={(e) => updateWhyItem(index, 'icon', e.target.value)} />
                </div>
                <div className="wc-field">
                  <label>Order</label>
                  <input
                    type="number"
                    value={item.order ?? index}
                    onChange={(e) => updateWhyItem(index, 'order', e.target.value)}
                  />
                </div>
                <div className="wc-field full">
                  <label>Description</label>
                  <textarea
                    rows={2}
                    value={item.description || ''}
                    onChange={(e) => updateWhyItem(index, 'description', e.target.value)}
                  />
                </div>
              </div>
              <button type="button" className="wc-btn danger" onClick={() => removeWhyItem(index)}>
                Remove card
              </button>
            </div>
          ))}
        </section>

        <section className="wc-card">
          <h2>Success Stories</h2>
          <div className="wc-grid-2">
            <div className="wc-field">
              <label>Title</label>
              <input
                value={form.successStories.title || ''}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    successStories: { ...prev.successStories, title: e.target.value },
                  }))
                }
              />
            </div>
            <div className="wc-field">
              <label>Subtitle</label>
              <input
                value={form.successStories.subtitle || ''}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    successStories: { ...prev.successStories, subtitle: e.target.value },
                  }))
                }
              />
            </div>
          </div>
          <div className="wc-subhead">
            <h3>Stories</h3>
            <button type="button" className="wc-btn ghost" onClick={addStory}>
              + Add story
            </button>
          </div>
          {(form.successStories.items || []).map((item, index) => (
            <div className="wc-item" key={`story-${index}`}>
              <div className="wc-grid-3">
                <div className="wc-field">
                  <label>Name</label>
                  <input value={item.name || ''} onChange={(e) => updateStory(index, 'name', e.target.value)} />
                </div>
                <div className="wc-field">
                  <label>Role</label>
                  <input value={item.role || ''} onChange={(e) => updateStory(index, 'role', e.target.value)} />
                </div>
                <div className="wc-field">
                  <label>Vendor name</label>
                  <input
                    value={item.vendorName || ''}
                    onChange={(e) => updateStory(index, 'vendorName', e.target.value)}
                  />
                </div>
                <div className="wc-field">
                  <label>Rating (1–5)</label>
                  <input
                    type="number"
                    min="1"
                    max="5"
                    value={item.rating ?? 5}
                    onChange={(e) => updateStory(index, 'rating', e.target.value)}
                  />
                </div>
                <div className="wc-field">
                  <label>Image URL</label>
                  <input value={item.image || ''} onChange={(e) => updateStory(index, 'image', e.target.value)} />
                </div>
                <div className="wc-field">
                  <label>Active</label>
                  <select
                    value={item.isActive === false ? 'false' : 'true'}
                    onChange={(e) => updateStory(index, 'isActive', e.target.value === 'true')}
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
                <div className="wc-field full">
                  <label>Quote</label>
                  <textarea
                    rows={2}
                    value={item.quote || ''}
                    onChange={(e) => updateStory(index, 'quote', e.target.value)}
                  />
                </div>
              </div>
              <button type="button" className="wc-btn danger" onClick={() => removeStory(index)}>
                Remove story
              </button>
            </div>
          ))}
        </section>

        <div className="wc-actions">
          <button type="submit" className="wc-btn primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save Website Content'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default WebsiteContent;
